import React, { useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

export type ModalSize = 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | 'full';

interface ModalProps {
    isOpen: boolean;
    onClose: () => void;
    title?: string;
    children: React.ReactNode;
    size?: ModalSize;
    /** Hide the default header (title and close button). Useful for custom headers. */
    hideHeader?: boolean;
    /** Custom className for the modal container */
    className?: string;
    /** Custom className for the body content area */
    bodyClassName?: string;
    /** Whether to show the close button in the header. Default: true */
    showCloseButton?: boolean;
    /** Whether clicking the backdrop closes the modal. Default: true */
    closeOnBackdropClick?: boolean;
    /** Whether pressing Escape closes the modal. Default: true */
    closeOnEscape?: boolean;
    /** Optional header content to render instead of or alongside the title */
    headerContent?: React.ReactNode;
    /** Optional footer content */
    footer?: React.ReactNode;
    /** Max height constraint. Use 'screen' for viewport height constraint */
    maxHeight?: 'none' | 'screen';
    /** Whether to trap focus within the modal. Default: true */
    trapFocus?: boolean;
    /** Whether to restore focus to the previously focused element on close. Default: true */
    restoreFocusOnClose?: boolean;
    /** Custom ID for aria-labelledby. Use when providing custom headerContent with your own title element. */
    ariaLabelledBy?: string;
    /** Accessible description for the modal. Use for additional context beyond the title. */
    ariaDescribedBy?: string;
}

export const Modal: React.FC<ModalProps> = ({
    isOpen,
    onClose,
    title,
    children,
    size = 'md',
    hideHeader = false,
    className = '',
    bodyClassName = '',
    showCloseButton = true,
    closeOnBackdropClick = true,
    closeOnEscape = true,
    headerContent,
    footer,
    maxHeight = 'none',
    trapFocus = true,
    restoreFocusOnClose = true,
    ariaLabelledBy,
    ariaDescribedBy,
}) => {
    const { theme } = useTheme();
    const modalRef = useRef<HTMLDivElement>(null);
    const previousActiveElement = useRef<HTMLElement | null>(null);

    // Get all focusable elements within the modal
    const getFocusableElements = useCallback((): HTMLElement[] => {
        if (!modalRef.current) return [];

        const selector = [
            'a[href]',
            'button:not([disabled])',
            'input:not([disabled])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            '[tabindex]:not([tabindex="-1"])',
        ].join(', ');

        const elements = modalRef.current.querySelectorAll<HTMLElement>(selector);
        return Array.from(elements).filter(el => {
            // Filter out hidden elements
            return el.offsetParent !== null && !el.hasAttribute('aria-hidden');
        });
    }, []);

    // Handle keyboard events (Escape and Tab for focus trapping)
    const handleKeyDown = useCallback((event: KeyboardEvent) => {
        // Handle Escape key
        if (event.key === 'Escape' && closeOnEscape) {
            event.preventDefault();
            onClose();
            return;
        }

        // Handle Tab key for focus trapping
        if (event.key === 'Tab' && trapFocus) {
            const focusableElements = getFocusableElements();
            if (focusableElements.length === 0) return;

            const firstElement = focusableElements[0];
            const lastElement = focusableElements[focusableElements.length - 1];

            // Shift+Tab: If focus is on first element, wrap to last
            if (event.shiftKey && document.activeElement === firstElement) {
                event.preventDefault();
                lastElement.focus();
                return;
            }

            // Tab: If focus is on last element, wrap to first
            if (!event.shiftKey && document.activeElement === lastElement) {
                event.preventDefault();
                firstElement.focus();
                return;
            }

            // If focus is outside the modal, move it inside
            if (!modalRef.current?.contains(document.activeElement)) {
                event.preventDefault();
                firstElement.focus();
            }
        }
    }, [closeOnEscape, onClose, trapFocus, getFocusableElements]);

    // Save and restore focus
    useEffect(() => {
        if (isOpen && restoreFocusOnClose) {
            // Save the currently focused element when opening
            previousActiveElement.current = document.activeElement as HTMLElement;
        }
    }, [isOpen, restoreFocusOnClose]);

    // Restore focus when closing
    useEffect(() => {
        if (!isOpen && restoreFocusOnClose && previousActiveElement.current) {
            // Use setTimeout to ensure this happens after the modal fully closes
            const elementToFocus = previousActiveElement.current;
            setTimeout(() => {
                if (elementToFocus && typeof elementToFocus.focus === 'function') {
                    elementToFocus.focus();
                }
            }, 0);
            previousActiveElement.current = null;
        }
    }, [isOpen, restoreFocusOnClose]);

    // Body scroll lock and keyboard listener
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
            document.addEventListener('keydown', handleKeyDown);
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [isOpen, handleKeyDown]);

    // Focus the first focusable element when modal opens
    useEffect(() => {
        if (isOpen && modalRef.current) {
            // Small delay to ensure modal animation has started
            requestAnimationFrame(() => {
                const focusableElements = getFocusableElements();
                if (focusableElements.length > 0) {
                    focusableElements[0].focus();
                } else {
                    modalRef.current?.focus();
                }
            });
        }
    }, [isOpen, getFocusableElements]);

    if (!isOpen) return null;

    const sizeClasses: Record<ModalSize, string> = {
        sm: 'max-w-sm',
        md: 'max-w-md',
        lg: 'max-w-lg',
        xl: 'max-w-xl',
        '2xl': 'max-w-2xl',
        '3xl': 'max-w-3xl',
        '4xl': 'max-w-4xl',
        '5xl': 'max-w-5xl',
        '6xl': 'max-w-6xl',
        full: 'max-w-full mx-4',
    };

    const handleBackdropClick = () => {
        if (closeOnBackdropClick) {
            onClose();
        }
    };

    const isDark = theme === 'dark';

    // Determine the correct aria-labelledby value:
    // 1. Use custom ariaLabelledBy if provided (for modals with custom headers)
    // 2. Fall back to 'modal-title' if a title prop is provided
    // 3. Otherwise undefined (screen readers will use modal content)
    const effectiveAriaLabelledBy = ariaLabelledBy ?? (title ? 'modal-title' : undefined);

    return (
        <div
            className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 ${isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby={effectiveAriaLabelledBy}
            aria-describedby={ariaDescribedBy}
        >
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
                onClick={handleBackdropClick}
                aria-hidden="true"
            />

            {/* Modal Content */}
            <div
                ref={modalRef}
                tabIndex={-1}
                className={`
                    relative w-full rounded-2xl shadow-2xl transform transition-all duration-300
                    ${isOpen ? 'scale-100 opacity-100 translate-y-0' : 'scale-95 opacity-0 translate-y-4'}
                    ${sizeClasses[size]}
                    ${maxHeight === 'screen' ? 'max-h-[90vh] flex flex-col' : ''}
                    ${isDark
                        ? 'bg-storgia-dark border border-storgia-light-blurple/20'
                        : 'bg-white'
                    }
                    ${className}
                `}
            >
                {/* Header */}
                {!hideHeader && (title || headerContent || showCloseButton) && (
                    <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? 'border-storgia-light-blurple/10' : 'border-gray-100'
                        }`}>
                        {headerContent || (
                            <h3
                                id="modal-title"
                                className={`text-lg font-bold font-league-spartan ${isDark ? 'text-white' : 'text-gray-900'}`}
                            >
                                {title}
                            </h3>
                        )}
                        {showCloseButton && (
                            <button
                                onClick={onClose}
                                aria-label="Close modal"
                                className={`p-2 rounded-full transition-colors ${isDark
                                    ? 'hover:bg-white/10 text-gray-400 hover:text-white'
                                    : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                <X className="h-5 w-5" />
                            </button>
                        )}
                    </div>
                )}

                {/* Body */}
                <div className={`p-6 ${maxHeight === 'screen' ? 'overflow-y-auto flex-1' : ''} ${bodyClassName}`}>
                    {children}
                </div>

                {/* Footer */}
                {footer && (
                    <div className={`px-6 py-4 border-t ${isDark ? 'border-storgia-light-blurple/10' : 'border-gray-100'}`}>
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
};
